import type {
  ConversationalFormOption,
  ConversationalFormStep,
} from '@karehero/models';
import type { ChoiceStyle } from '@karehero/llama/pwa/pages/ConversationalFormPage/ConversationalFormPage';

import * as copy from 'config/onboardingV4/copy';
import type { TalkOpening } from 'config/onboardingV4/flows';
import type { TriageQuestion } from 'config/triageQuestions';
import { phrase, type Person } from 'utils/person';

// What each beat of onboarding v4 asks, and the answers it offers, as designed.
// Whether a beat's step is shown yet is utils/onboardingV4Steps' to say.

export const WELCOME_START = 'start';
export const CONSENT_AGREE = 'agree';
export const CONSENT_DECLINE = 'decline';
export const CONSENT_AI = 'ai';
export const HEARD_YES = 'yes';
export const HEARD_NOT_QUITE = 'not-quite';
export const YES = 'yes';
export const NOT_NOW = 'not-now';
export const GATHER_DONE = 'done';
export const GATHER_LATER = 'later';
export const START_CLAIM = 'claim';
export const START_CARE = 'care';
export const START_EXPERT = 'expert';
export const START_LATER = 'later';
export const START_BOOK_CALL = 'book-call';
export const PHONE_LATER = 'later';
export const UNDER_18_STEP = 'under-18';
export const CHANGE_AGE = 'change-age';

/** A chat step as designed; voice is offered where the step has it and the flags and consent allow. */
export interface BeatStep extends Omit<
  ConversationalFormStep,
  'presentation' | 'isVoiceEnabled' | 'isFileEnabled' | 'placeholder'
> {
  hasVoice?: boolean;
  /** How its answers are laid out, where the step says rather than the beat and flow. */
  choiceStyle?: ChoiceStyle;
}

export const WHO_OPTIONS: ConversationalFormOption[] = [
  { id: 'someone-else', label: copy.WHO.someoneElse },
  { id: 'myself', label: copy.WHO.myself },
  { id: 'exploring', label: copy.WHO.exploring, isPrimary: false },
];

/** The screens before the chat; Keni is added to the welcome where the flow shows her. */
export const WELCOME_SCREENS = {
  welcome: {
    id: 'welcome',
    presentation: 'screen',
    mode: 'choose',
    title: copy.WELCOME.title,
    ask: copy.WELCOME.body,
    options: [
      { id: WELCOME_START, label: copy.WELCOME.action, isPrimary: true },
    ],
  },
  consent: {
    id: 'ai-consent',
    presentation: 'screen',
    mode: 'choose',
    title: copy.CONSENT.title,
    ask: copy.CONSENT.body,
    points: copy.CONSENT.points,
    note: {
      lead: copy.CONSENT.noteLead,
      linkLabel: copy.CONSENT.noteLink,
      linkUrl: copy.AI_POLICY_URL,
    },
    options: [
      { id: CONSENT_AGREE, label: copy.CONSENT.agree, isPrimary: true },
      { id: CONSENT_DECLINE, label: copy.CONSENT.decline },
    ],
  },
} satisfies Record<string, ConversationalFormStep>;

const PROMPTED_TALK: BeatStep = {
  id: 'talk',
  mode: 'expand',
  ask: copy.TALK.ask,
  hasVoice: true,
};

/** The talk's open ask, by how the flow opens it. */
export const TALK_STEPS: Record<TalkOpening, BeatStep> = {
  prompted: PROMPTED_TALK,
  open: { ...PROMPTED_TALK, ask: copy.OPENING.ask },
};

/** Each triage question, asked in the voice for them with the calculator's answers. */
export const questionStep = (
  question: TriageQuestion,
  person: Person,
): BeatStep => ({
  id: question.id,
  mode: 'say',
  ask: phrase(question.ask, person),
  options: question.options,
  hasVoice: true,
});

export const BEAT_STEPS = {
  /** Consent as Keni's first message; declining is quieter, and How we use AI explains rather than answers. */
  consent: {
    id: 'ai-consent',
    mode: 'choose',
    ask: copy.CONSENT_IN_CHAT.ask,
    options: [
      { id: CONSENT_AGREE, label: copy.CONSENT.agree },
      { id: CONSENT_DECLINE, label: copy.CONSENT.decline, isQuiet: true },
      {
        id: CONSENT_AI,
        label: copy.CONSENT_IN_CHAT.ai.label,
        isQuiet: true,
        explain: {
          title: copy.CONSENT_IN_CHAT.ai.label,
          points: copy.CONSENT_IN_CHAT.ai.points,
          note: {
            lead: copy.CONSENT_IN_CHAT.ai.noteLead,
            linkLabel: copy.CONSENT_IN_CHAT.ai.noteLink,
            linkUrl: copy.AI_POLICY_URL,
          },
        },
      },
    ],
  },
  who: {
    id: 'who',
    mode: 'choose',
    ask: copy.WHO.ask,
    options: WHO_OPTIONS,
  },
  /** Read back once a round; its id names the round. */
  heard: {
    id: 'heard',
    mode: 'say',
    ask: copy.HEARD.ask,
    hasVoice: true,
    options: [
      { id: HEARD_YES, label: copy.HEARD.yes },
      { id: HEARD_NOT_QUITE, label: copy.HEARD.notQuite, isPrimary: false },
    ],
  },
  /** After "Not quite"; its id names the round. */
  correcting: {
    id: 'heard:correcting',
    mode: 'expand',
    ask: copy.HEARD.whatsWrong,
    hasVoice: true,
  },
  help: {
    id: 'help',
    mode: 'say',
    ask: copy.HELP.ask,
    hasVoice: true,
    options: [
      { id: YES, label: copy.HELP.yes },
      { id: NOT_NOW, label: copy.HELP.notNow, isPrimary: false },
    ],
  },
  need: {
    id: 'need',
    mode: 'say',
    ask: copy.NEED.ask,
    hasVoice: true,
    options: [
      { id: YES, label: copy.NEED.yes },
      { id: NOT_NOW, label: copy.NEED.later, isPrimary: false },
    ],
  },
  gather: {
    id: 'gather',
    mode: 'expand',
    ask: copy.GATHER.ask,
    hasVoice: true,
    options: [
      { id: GATHER_DONE, label: copy.GATHER.done },
      { id: GATHER_LATER, label: copy.GATHER.later, isPrimary: false },
    ],
  },
  /** What they need help with, in their own words, read with the summary again. */
  helpWith: {
    id: 'help-with',
    mode: 'say',
    ask: copy.HELP_WITH.ask,
    hasVoice: true,
  },
  phone: {
    id: 'phone',
    mode: 'say',
    ask: copy.PHONE.ask,
    inputType: 'tel',
    options: [{ id: PHONE_LATER, label: copy.PHONE.later, isPrimary: false }],
  },
  /** Its places to start are startChoices, from what is known. */
  start: { id: 'start', mode: 'choose', ask: copy.START.ask },
} satisfies Record<string, BeatStep>;

/** What the places to start turn on. */
export interface StartFacts {
  needsHelp: boolean;
}

/** Where they could start: no care at home for someone needing no help. */
export const startChoices = (
  person: Person,
  facts: StartFacts,
): ConversationalFormOption[] => [
  { id: START_CLAIM, label: phrase(copy.START.claim, person) },
  ...(facts.needsHelp ? [{ id: START_CARE, label: copy.START.care }] : []),
  { id: START_EXPERT, label: copy.START.expert },
  { id: START_LATER, label: copy.START.later, isPrimary: false, isQuiet: true },
];

/** Where to start where it books a call: that, or later. */
export const CALL_CHOICES: ConversationalFormOption[] = [
  { id: START_BOOK_CALL, label: copy.START.bookCall },
  { id: START_LATER, label: copy.START.later, isPrimary: false, isQuiet: true },
];

/** The kind stop for an under 18, with the age to change in case it was a slip, on its message whichever beat it comes in. */
export const under18Step = (person: Person): BeatStep => ({
  id: UNDER_18_STEP,
  mode: 'choose',
  ask: phrase(copy.UNDER_18.ask, person),
  options: [{ id: CHANGE_AGE, label: copy.UNDER_18.changeAge }],
  choiceStyle: 'attached',
});
